import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { notificationsApi } from './notificationsApi';
import './notifications.css';

const POLL_MS = 60000;

const KIND_ICON = {
  ApprovalActionNeeded: Icon.ClipboardList,
  ApprovalReminder: Icon.Clock,
  ApprovalEscalation: Icon.CircleAlert,
  ApprovalApproved: Icon.Check,
  ApprovalRejected: Icon.CircleAlert,
  DeliveryOverdue: Icon.Truck,
  LowStock: Icon.Boxes,
  CommentAdded: Icon.MessageSquare,
  CommentMention: Icon.AtSign,
};

const KIND_TONE = {
  ApprovalActionNeeded: 'brand',
  ApprovalReminder: 'warn',
  ApprovalEscalation: 'danger',
  ApprovalApproved: 'ok',
  ApprovalRejected: 'danger',
  DeliveryOverdue: 'warn',
  LowStock: 'warn',
  CommentAdded: 'brand',
  CommentMention: 'brand',
};

function timeAgo(iso, t) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return t('just now');
  const mins = Math.floor(secs / 60);
  if (mins < 60) return t('{n}m ago', { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('{n}h ago', { n: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 7) return t('{n}d ago', { n: days });
  return new Date(iso).toLocaleDateString();
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const res = await notificationsApi.unreadCount();
      setUnread(res?.count ?? 0);
    } catch {
      /* advisory only */
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationsApi.list({ pageSize: 15 });
      setItems(res?.items ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, POLL_MS);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      if (next) loadList();
      return next;
    });
  };

  const openItem = async (n) => {
    setOpen(false);
    if (!n.isRead) {
      try {
        await notificationsApi.markRead(n.id, true);
        setUnread((u) => Math.max(0, u - 1));
        setItems((list) => list.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
      } catch {
        /* ignore */
      }
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await notificationsApi.markAllRead();
      setUnread(0);
      setItems((list) => list.map((x) => ({ ...x, isRead: true })));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="pp-notif" ref={ref}>
      <button
        type="button"
        className="pp-notif__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={unread > 0 ? t('Notifications, {count} unread', { count: unread }) : t('Notifications')}
        onClick={toggle}
        title={t('Notifications')}
      >
        <Icon.Bell size={18} />
        {unread > 0 && <span className="pp-notif__badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="pp-notif__menu" role="menu">
          <div className="pp-notif__head">
            <span className="pp-notif__title">{t('Notifications')}</span>
            {unread > 0 && (
              <button type="button" className="pp-notif__markall" onClick={markAll}>
                {t('Mark all read')}
              </button>
            )}
          </div>

          <div className="pp-notif__list">
            {loading && <div className="pp-notif__empty">{t('Loading…')}</div>}
            {!loading && items.length === 0 && (
              <div className="pp-notif__empty">
                <Icon.Bell size={22} />
                <span>{t('You’re all caught up')}</span>
              </div>
            )}
            {!loading &&
              items.map((n) => {
                const KindIcon = KIND_ICON[n.kind] || Icon.Bell;
                const tone = KIND_TONE[n.kind] || 'brand';
                return (
                  <button
                    key={n.id}
                    type="button"
                    className={`pp-notif__item${n.isRead ? '' : ' is-unread'}`}
                    onClick={() => openItem(n)}
                  >
                    <span className={`pp-notif__icon pp-notif__icon--${tone}`}>
                      <KindIcon size={15} />
                    </span>
                    <span className="pp-notif__body">
                      <span className="pp-notif__item-title">{n.title}</span>
                      {n.message && <span className="pp-notif__item-msg">{n.message}</span>}
                      <span className="pp-notif__item-time">{timeAgo(n.createdAt, t)}</span>
                    </span>
                    {!n.isRead && <span className="pp-notif__dot" aria-hidden="true" />}
                  </button>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
